import { AfterViewInit, Directive, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { of } from 'rxjs';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import { debounceTime, delay, filter, map, pairwise, tap } from 'rxjs/operators';

@Directive({
  selector: '[fbInfiniteListScroll]',
  exportAs: 'infiniteListScroll',
})
export class InfiniteListScrollDirective extends OnDestroyMixin implements OnInit, AfterViewInit {
  @Input() threshold: string = '100';
  @Input() endOfData: boolean;
  @Output() scrollEvent: EventEmitter<void> = new EventEmitter();

  get panel(): HTMLElement {
    return this.element.nativeElement;
  }

  private thrPx: number = 0;
  private thrPc: number = 0;

  constructor(private element: ElementRef) {
    super();
  }

  ngOnInit(): void {
    this.evaluateThreshold();
  }

  ngAfterViewInit(): void {
    this.registerScrollListener();
  }

  evaluateThreshold(): void {
    if (this.threshold.lastIndexOf('%') > -1) {
      this.thrPx = 0;
      this.thrPc = parseFloat(this.threshold) / 100;
    } else {
      this.thrPx = parseFloat(this.threshold);
      this.thrPc = 0;
    }
  }

  registerScrollListener(): void {
    const delayBeforeNextLoad: number = 100;
    fromEvent(this.panel, 'scroll')
      .pipe(
        map((event: Event) => (event?.target as Element)?.scrollTop),
        pairwise<number>(),
        filter(([scrollTop1, scrollTop2]: [number, number]) => scrollTop1 <= scrollTop2),
        filter(() => !this.endOfData),
        debounceTime(delayBeforeNextLoad),
        untilComponentDestroyed(this),
        tap(([, scrollTop]: [number, number]) => {
          this.handleScrollEvent(scrollTop);
        }),
      )
      .subscribe();
  }

  handleScrollEvent(scrollTop: number): void {
    const threshold: number = this.thrPc !== 0 ? this.panel.clientHeight * this.thrPc : this.thrPx;
    const scrolledDistance: number = this.panel.scrollHeight - scrollTop - this.panel.clientHeight;

    if (scrolledDistance < threshold) {
      this.scrollEvent.emit();

      of(null)
        .pipe(untilComponentDestroyed(this), delay(100))
        .subscribe(() => {
          this.panel.scrollTop = this.panel.scrollHeight - this.panel.clientHeight;
        });
    }
  }
}
